/* Seitenleiste „Bauen“: Generatoren (Raum, Treppe, Felswand, Hügel, Bogen, Rundung, Kuppel) mit Feldern, Voreinstellungen und Vorschau der Brush-Anzahl. */
import { useReducer, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Cmd from '../../model/cmds.ts';
import { applyFix } from '../../model/checkfix.ts';
import type { Brush, BBox, Doc, Sel } from '../../model/types.ts';
import { GENS, genCount, genValues } from '../../model/genspecs.ts';
import type { GenField, GenSpec, Params } from '../../model/genspecs.ts';
import { useDebounced, useT } from '../hooks.ts';
import { TexSelect } from '../assets/KindSelect.tsx';
import { createNotifier, useNotifier } from '../notifier.ts';
import type { Notifier } from '../notifier.ts';
import { DEFAULTS, simplify, targets } from '../../model/simplify.ts';
import type { SimplifyOpts } from '../../model/simplify.ts';
import { DEFAULTS as SCULPT_DEFAULTS, sculpt } from '../../model/sculpt.ts';
import type { Dir, SculptOpts } from '../../model/sculpt.ts';

import type { AssetHost } from '../assets/host.ts';

export interface BuildApp {
  doc: Doc; sel: Sel; activeTex: string;
  status(msg: string): void;
  commit(doc: Doc, label: string, sel?: Sel): void;
  groupSel(name: string): void;
  selBBox(): BBox | null;
  /** Auswahlfelder für Texturen (fehlt in Tests) */
  assets?: AssetHost;
}
/** Eingaben der Generatoren und der geöffnete Generator; bleiben über das Neuzeichnen erhalten (und über den Tabwechsel) */
export interface BuildShared { gen: Record<string, Params>; genOpen: string }

const MAX_WARN = 1500, MAX_GO = 4000;

function Field(props: { g: GenSpec; f: GenField; shared: BuildShared; change: () => void; assets?: AssetHost }) {
  const t = useT();
  const { g, f, shared } = props, id = 'gen-' + g.id + '-' + f.k, val = shared.gen[g.id][f.k];
  const set = (v: string) => { shared.gen[g.id][f.k] = v; props.change(); };
  return (
    <>
      <label htmlFor={id}>{t(f.l)}</label>
      {f.t === 'sel' ? (
        <select id={id} className="sel" value={String(val)} onChange={(e) => set(e.target.value)}>{f.o?.map((o) => <option key={o[0]} value={o[0]}>{t(o[1])}</option>)}</select>
      ) : f.t === 'tex' && props.assets ? (
        <TexSelect host={props.assets} label={t(f.l)} value={String(val)} browse={false} onPick={(v) => set(v.trim())} />
      ) : f.t === 'tex' ? (
        <input id={id} type="text" className="inp" spellCheck={false} autoComplete="off" list="dl-tex" value={String(val)} onChange={(e) => set(e.target.value)} />
      ) : (
        <input id={id} type="number" className="inp" step="any" value={String(val)} onChange={(e) => set(e.target.value)} />
      )}
    </>
  );
}

function GenBlock(props: { g: GenSpec; app: BuildApp; shared: BuildShared }) {
  const t = useT();
  const { g, app, shared } = props;
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const [replace, setReplace] = useState(false);
  const [asDetail, setAsDetail] = useState(true);
  const preset = useRef<HTMLSelectElement>(null);
  // Die Brush-Anzahl braucht den Generator selbst und wird erst berechnet, wenn kurz nicht getippt wurde
  const key = useDebounced(JSON.stringify(shared.gen[g.id]), 150);
  const count = genCount(g, JSON.parse(key));

  const generate = () => {
    const brushes: Brush[] = g.make(genValues(g, shared.gen[g.id]));
    if (!brushes.length) { app.status(t('pn.gen_nothing')); return; }
    let doc = app.doc;
    if (replace && app.sel.b.size) doc = Cmd.remove(doc, app.sel).doc;
    const r = Cmd.addBrushList(doc, brushes);
    if (g.detail && asDetail) r.doc = applyFix(r.doc, 'detail', brushes.map((b) => b.id)).doc; // func_detail mit zhlt_detaillevel 1: Gelände aus Prismen bleibt außerhalb von VIS
    app.commit(r.doc, t('pn.gen_commit', { name: t(g.name) }), r.sel);
    app.groupSel(t(g.name)); // als Gruppe einfügen: ein Klick wählt alles, so bewegt es sich gemeinsam
    app.status(t('pn.gen_done', { name: t(g.name), count: brushes.length }));
  };
  const applyPreset = (name: string) => {
    const pr = g.presets?.find((x) => x.n === name);
    if (!pr) return;
    Object.assign(shared.gen[g.id], pr.v);
    shared.genOpen = g.id; bump(); app.status(t('pn.preset_applied', { name: t(pr.n) }));
    if (preset.current) preset.current.value = '';
  };
  const fromSel = () => {
    const bb = app.selBBox();
    if (!bb) { app.status(t('pn.select_brushes_first')); return; }
    g.fromSel(shared.gen[g.id], bb); setReplace(true); bump();
  };
  const withActiveTex = () => { g.f.filter((f) => f.t === 'tex').forEach((f) => { shared.gen[g.id][f.k] = app.activeTex; }); bump(); };

  return (
    <details className="gen" open={shared.genOpen === g.id} onToggle={(e) => { if ((e.currentTarget as HTMLDetailsElement).open) shared.genOpen = g.id; }}>
      <summary>{t(g.name)}</summary>
      <div className="body">
        <p className="small">{t(g.info)}</p>
        {g.presets && (
          <div className="row">
            <span className="lbl-s">{t('pn.preset')}</span>
            <select ref={preset} className="sel" defaultValue="" aria-label={t('pn.preset')} onChange={(e) => applyPreset(e.target.value)}>
              <option value="">{t('pn.choose')}</option>
              {g.presets.map((x) => <option key={x.n} value={x.n}>{t(x.n)}</option>)}
            </select>
          </div>
        )}
        <div className="fields">{g.f.map((f) => <Field key={f.k} g={g} f={f} shared={shared} change={bump} assets={app.assets} />)}</div>
        <div className="row">
          <button className="btn sm" title={t('pn.from_sel_tip')} onClick={fromSel}>{t('pn.from_sel')}</button>
          <button className="btn sm" title={t('pn.active_tex_tip')} onClick={withActiveTex}>{t('pn.active_tex_btn')}</button>
        </div>
        <label className="chk"><input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />{t('pn.replace_sel')}</label>
        {g.detail && <label className="chk"><input type="checkbox" checked={asDetail} onChange={(e) => setAsDetail(e.target.checked)} />{t('pn.gen_as_detail')}</label>}
        <div className="row sp">
          <span className="small" style={count < 0 || count > MAX_WARN ? { color: 'var(--warn)' } : undefined}>
            {count < 0 ? t('pn.gen_invalid') : '→ ' + t('pn.n_brushes', { count }) + (count > MAX_WARN ? ' ' + t('pn.gen_many') : '')}
          </span>
          <button className="btn sm primary" disabled={count <= 0 || count > MAX_GO} onClick={generate}>{t('pn.generate')}</button>
        </div>
      </div>
    </details>
  );
}

/** Gelände vereinfachen: weniger Brushes bei gleicher Form (siehe model/simplify.ts) */
function SimplifyBlock(props: { app: BuildApp }) {
  const t = useT();
  const { app } = props;
  const [o, setO] = useState<SimplifyOpts>(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const count = targets(app.doc, app.sel).length;
  const set = (p: Partial<SimplifyOpts>) => setO({ ...o, ...p });
  const num = (v: string, d: number) => { const n = parseFloat(v); return Number.isFinite(n) && n >= 0 ? n : d; };
  const run = () => {
    setBusy(true); app.status(t('pn.simp_running'));
    // erst zeichnen lassen, dann rechnen (große Karten brauchen einige Sekunden)
    setTimeout(() => {
      try {
        const r = simplify(app.doc, app.sel, o);
        if (!r.before) { app.status(t('pn.simp_nothing')); return; }
        app.commit(r.doc, t('pn.simp_commit'), r.sel);
        app.status(t('pn.simp_done', { before: r.before, after: r.after, removed: r.removed }) + (r.decimateFailed ? ' ' + t('pn.simp_failed') : ''));
      } finally { setBusy(false); }
    }, 30);
  };
  return (
    <details className="gen">
      <summary>{t('pn.simp_title')}</summary>
      <div className="body">
        <p className="small">{t('pn.simp_info')}</p>
        <p className="small">{t(app.sel.b.size ? 'pn.simp_scope_sel' : 'pn.simp_scope_all', { count })}</p>
        <div className="fields">
          <label htmlFor="simp-tol">{t('pn.simp_tol')}</label>
          <input id="simp-tol" type="number" className="inp" min={0} step="any" value={o.tol} onChange={(e) => set({ tol: num(e.target.value, o.tol) })} />
          <label htmlFor="simp-wtol">{t('pn.simp_wtol')}</label>
          <input id="simp-wtol" type="number" className="inp" min={0} step="any" value={o.waterTol} onChange={(e) => set({ waterTol: num(e.target.value, o.waterTol) })} />
        </div>
        <label className="chk"><input type="checkbox" checked={o.decimate} onChange={(e) => set({ decimate: e.target.checked })} />{t('pn.simp_decimate')}</label>
        <label className="chk"><input type="checkbox" checked={o.merge} onChange={(e) => set({ merge: e.target.checked })} />{t('pn.simp_merge')}</label>
        <label className="chk"><input type="checkbox" checked={o.detail} onChange={(e) => set({ detail: e.target.checked })} />{t('pn.simp_detail')}</label>
        <div className="row sp">
          <span />
          <button className="btn sm primary" disabled={busy || !count || (!o.decimate && !o.merge)} onClick={run}>{t('pn.simp_run')}</button>
        </div>
      </div>
    </details>
  );
}

/** Gelände formen: Kreis anheben, eindrücken oder glätten (siehe model/sculpt.ts) */
function SculptBlock(props: { app: BuildApp }) {
  const t = useT();
  const { app } = props;
  const [o, setO] = useState<SculptOpts>(SCULPT_DEFAULTS);
  const [onlySel, setOnlySel] = useState(false);
  const set = (p: Partial<SculptOpts>) => setO({ ...o, ...p });
  const num = (v: string, d: number) => { const n = parseFloat(v); return Number.isFinite(n) ? n : d; };
  const fromSel = () => {
    const bb = app.selBBox();
    if (!bb) { app.status(t('pn.sculpt_nosel')); return; }
    set({ cx: Math.round((bb.min[0] + bb.max[0]) / 2), cy: Math.round((bb.min[1] + bb.max[1]) / 2) });
  };
  const run = (sign: number) => {
    const smooth = o.mode === 'smooth';
    const r = sculpt(app.doc, onlySel ? app.sel : null, smooth ? o : { ...o, strength: Math.abs(o.strength) * sign });
    if (!r.moved) { app.status(r.rejected ? t('pn.sculpt_rejected', { count: r.rejected }) : t('pn.sculpt_nothing')); return; }
    app.commit(r.doc, t('pn.sculpt_commit'));
    app.status(t('pn.sculpt_done', { moved: r.moved, count: r.brushes }) + (r.limited ? ' ' + t('pn.sculpt_limited', { count: r.limited }) : ''));
  };
  return (
    <details className="gen">
      <summary>{t('pn.sculpt_title')}</summary>
      <div className="body">
        <p className="small">{t('pn.sculpt_info')}</p>
        <div className="fields">
          <label htmlFor="sc-x">X</label>
          <input id="sc-x" type="number" className="inp" step="any" value={o.cx} onChange={(e) => set({ cx: num(e.target.value, o.cx) })} />
          <label htmlFor="sc-y">Y</label>
          <input id="sc-y" type="number" className="inp" step="any" value={o.cy} onChange={(e) => set({ cy: num(e.target.value, o.cy) })} />
          <label htmlFor="sc-z">Z</label>
          <input id="sc-z" type="number" className="inp" step="any" placeholder={t('pn.sculpt_z_all')} value={o.cz ?? ''} onChange={(e) => { const n = parseFloat(e.target.value); set({ cz: Number.isFinite(n) ? n : undefined }); }} />
          <label htmlFor="sc-dir">{t('pn.sculpt_dir')}</label>
          <select id="sc-dir" className="sel" value={o.dir ?? 0} onChange={(e) => set({ dir: parseInt(e.target.value, 10) as Dir })}>
            <option value={0}>{t('pn.sculpt_dir0')}</option>
            <option value={1}>{t('pn.sculpt_dir1')}</option>
            <option value={2}>{t('pn.sculpt_dir2')}</option>
            <option value={3}>{t('pn.sculpt_dir3')}</option>
            <option value={4}>{t('pn.sculpt_dir4')}</option>
            <option value={5}>{t('pn.sculpt_dir5')}</option>
          </select>
          <label htmlFor="sc-r">{t('pn.sculpt_radius')}</label>
          <input id="sc-r" type="number" className="inp" min={1} step="any" value={o.radius} onChange={(e) => set({ radius: Math.max(1, num(e.target.value, o.radius)) })} />
          <label htmlFor="sc-mode">{t('pn.sculpt_mode')}</label>
          <select id="sc-mode" className="sel" value={o.mode} onChange={(e) => set({ mode: e.target.value as SculptOpts['mode'], strength: e.target.value === 'smooth' ? 0.5 : 16 })}>
            <option value="push">{t('pn.sculpt_push')}</option>
            <option value="smooth">{t('pn.sculpt_smooth')}</option>
          </select>
          <label htmlFor="sc-s">{t(o.mode === 'smooth' ? 'pn.sculpt_smooth_str' : 'pn.sculpt_strength')}</label>
          <input id="sc-s" type="number" className="inp" min={0} step="any" value={o.strength} onChange={(e) => set({ strength: Math.max(0, num(e.target.value, o.strength)) })} />
        </div>
        <label className="chk"><input type="checkbox" checked={onlySel} onChange={(e) => setOnlySel(e.target.checked)} />{t('pn.sculpt_only_sel')}</label>
        <div className="row sp">
          <button className="btn sm" title={t('pn.sculpt_center_tip')} onClick={fromSel}>{t('pn.sculpt_center')}</button>
          {o.mode === 'smooth'
            ? <button className="btn sm primary" onClick={() => run(1)}>{t('pn.sculpt_smooth')}</button>
            : <span className="row"><button className="btn sm primary" onClick={() => run(1)}>{t('pn.sculpt_up')}</button><button className="btn sm primary" onClick={() => run(-1)}>{t('pn.sculpt_down')}</button></span>}
        </div>
      </div>
    </details>
  );
}

export function GeneratorsPanel(props: { app: BuildApp; shared: BuildShared; notifier: Notifier }) {
  const t = useT();
  useNotifier(props.notifier);
  return (
    <>
      <p className="small">{t('pn.gen_intro')}</p>
      {GENS.map((g) => <GenBlock key={g.id} g={g} app={props.app} shared={props.shared} />)}
      <SculptBlock app={props.app} />
      <SimplifyBlock app={props.app} />
    </>
  );
}

export function mountGeneratorsPanel(el: HTMLElement, app: BuildApp, shared: BuildShared): { refresh(): void; unmount(): void } {
  const notifier = createNotifier();
  const root = createRoot(el);
  el.textContent = '';
  root.render(<GeneratorsPanel app={app} shared={shared} notifier={notifier} />);
  return { refresh: notifier.notify, unmount: () => root.unmount() };
}
